import * as I from 'immutable';

import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import * as CONSTANTS from 'app/utils/constants';
import {SYSTEM_USER_ID} from 'app/utils/constants';

import {isInternalOrgMemberViewingAnyOrg} from './featureFlags';
import {useKeyPress} from './hookUtils';

export const UPSTREAM_TEST_ORG_ID = '-KQ66NhS23Am_QKkk7CR';

export function isUpstreamTeamEmail(email: string | null) {
  return /^team\+\S*@upstream.tech$/.test(email || '');
}

export function isSystemUserId(userId: string) {
  return userId === SYSTEM_USER_ID;
}

export function isUpstreamEmail(email: string | null) {
  return /^\S*@upstream.tech$/.test(email || '');
}

export function showInternalTools(
  profile: I.ImmutableOf<ApiOrganizationUser>,
  organization: I.ImmutableOf<ApiOrganization>,
  optionKey?: boolean
) {
  return (
    ([UPSTREAM_TEST_ORG_ID].includes(organization.get('id')) || optionKey !== false) &&
    isUpstreamEmail(profile.get('email'))
  );
}

// Check if alt button is pressed, and then if user is allowed to see internal tools
export function useShowInternalToolsOnAltPress(profile: I.ImmutableOf<ApiOrganizationUser>) {
  const altPressed = useKeyPress(['AltLeft', 'AltRight']);
  return altPressed && isInternalOrgMemberViewingAnyOrg(profile);
}

export function showAnalyzeAreaInternalTools(
  profile: I.ImmutableOf<ApiOrganizationUser>,
  organization: I.ImmutableOf<ApiOrganization>,
  optionKey?: boolean
) {
  return (
    ([UPSTREAM_TEST_ORG_ID].includes(organization.get('id')) || optionKey !== false) &&
    isUpstreamEmail(profile.get('email'))
  );
}

/**
 * The `admin` role is owner-equivalent: it should pass every permission check
 * that `owner` passes. Prefer this over comparing a role against `owner`
 * directly so the two stay in lockstep.
 */
export function isOwnerOrAdmin(role: ApiOrganizationUser['role']) {
  return role === CONSTANTS.USER_ROLE_OWNER || role === CONSTANTS.USER_ROLE_ADMIN;
}

export function mayOrderImagery(role: ApiOrganizationUser['role']) {
  return isOwnerOrAdmin(role);
}

/**
 * Returns true if the user is currently accessing Lens as their logged-in Organization,
 * which is a parent org. Behavior changes a little for the internal org.
 */
export function shouldSeeOrgSwitcher(
  _currentOrganization: I.ImmutableOf<ApiOrganization>,
  profileOrganizations: I.List<I.MapAsRecord<I.ImmutableFields<ApiOrganization>>>
) {
  const profileOrg = profileOrganizations.get(0);
  const hasMultipleProfileOrgs = profileOrganizations.size > 1;

  const isInternalOrgUser = profileOrg.get('id') == CONSTANTS.INTERNAL_ORGANIZATION_ID;
  const childOrgs = profileOrg.get('childOrganizations');
  const profileOrgIsAParentOrg = childOrgs.size > 0;
  return isInternalOrgUser
    ? true // always show org switcher if they're from the internal org
    : profileOrgIsAParentOrg || hasMultipleProfileOrgs;
}

// Used in NavSidebar to know if we should disable some settings pages for parent organizations
export function shouldTreatAsParentOrg(
  profile: I.ImmutableOf<ApiOrganizationUser>,
  currentOrganization: I.ImmutableOf<ApiOrganization>,
  profileOrganizations: I.List<I.MapAsRecord<I.ImmutableFields<ApiOrganization>>>
) {
  const userHomeOrganizationKey = profile.get('organizationKey'); // where the user account was created
  const profileOrganization = profileOrganizations.find(
    (org) => org?.get('id') === userHomeOrganizationKey
  );
  return (
    profileOrganization.get('childOrganizations').size > 0 &&
    profileOrganization.get('id') === currentOrganization.get('id') &&
    currentOrganization.get('id') !== CONSTANTS.INTERNAL_ORGANIZATION_ID
  );
}

export function getSuffixedUserName(user: ApiOrganizationUser) {
  const suffix = user.role === CONSTANTS.USER_ROLE_READONLY ? ' (read-only)' : '';
  return user.name + suffix;
}
const USER_ROLE_LEVELS: ApiOrganizationUser['role'][] = [
  CONSTANTS.USER_ROLE_READONLY,
  CONSTANTS.USER_ROLE_REGULAR,
  CONSTANTS.USER_ROLE_ADMIN,
  CONSTANTS.USER_ROLE_OWNER,
];
/**
 * Returns true if the user is at the given role or higher. Assumes the roles
 * are all strictly ranked, so members don't ever get anything that owners don't
 * have.
 */

export function atLeastUserRole(
  targetRole: ApiOrganizationUser['role'],
  profile: I.ImmutableOf<ApiOrganizationUser>
) {
  const role = profile.get('role');
  const roleLevel = USER_ROLE_LEVELS.indexOf(role);

  const targetRoleLevel = USER_ROLE_LEVELS.indexOf(targetRole);
  return roleLevel >= targetRoleLevel;
}
